// §5.1 / §5.2 — pure rect math.
//
// Nothing here touches the store or the DOM: every function takes rects and returns rects, so
// placement, clamping and restore behavior are testable without a window manager and reusable
// by the drag session, the snap engine and session restore alike.
import type { Rect } from '@hangar/shared';

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

/** Pixels a cascaded window steps down-and-right from the previous one. */
export const CASCADE_STEP = 28;
/** How many steps before the cascade returns to the origin instead of marching off-screen. */
export const CASCADE_LIMIT = 5;
/** Distance a cascade origin sits from the work-area corner. */
export const CASCADE_ORIGIN = 32;

export function rectsEqual(a: Rect, b: Rect): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/** The area a window may occupy: the display minus whatever chrome the skin reserves. */
export function workAreaFor(display: Rect, insets: Insets = NO_INSETS): Rect {
  return {
    x: display.x + insets.left,
    y: display.y + insets.top,
    w: Math.max(0, display.w - insets.left - insets.right),
    h: Math.max(0, display.h - insets.top - insets.bottom),
  };
}

/**
 * Pull a rect fully inside `area`. Size is reduced first — a window wider than the display can
 * never be both fully visible and its requested size, and being visible matters more.
 */
export function clampToDisplay(rect: Rect, area: Rect, min?: { w: number; h: number }): Rect {
  const minW = Math.min(min?.w ?? 0, area.w);
  const minH = Math.min(min?.h ?? 0, area.h);
  const w = Math.max(minW, Math.min(rect.w, area.w));
  const h = Math.max(minH, Math.min(rect.h, area.h));
  return {
    x: Math.min(Math.max(rect.x, area.x), area.x + area.w - w),
    y: Math.min(Math.max(rect.y, area.y), area.y + area.h - h),
    w,
    h,
  };
}

/**
 * How much of a window must stay inside the work area horizontally, in pixels (D-438).
 *
 * Enough to grab and to read a title from, and no more: the rule exists to keep a window
 * *recoverable*, not to forbid parking one mostly off the side of the screen.
 */
export const REACHABLE_EDGE = 64;

/** How much of a window must stay above the bottom edge — a title bar's worth. */
export const REACHABLE_TOP = 28;

/**
 * Pull a rect back until it can be reached again, **without resizing it** (D-438).
 *
 * This is deliberately weaker than `clampToDisplay`. It runs whenever the work area changes — which
 * includes every browser resize and, crucially, boot — so it must disturb a layout only when that
 * layout has become unusable. Shrinking windows to fit a smaller viewport would lose the size the
 * player chose, every time they resized the browser.
 *
 * The invariant it does keep is the one that matters: **the title bar is always on screen.** Its top
 * is never above the work area, never below it, and at least `REACHABLE_EDGE` pixels of the window
 * remain inside horizontally. Everything else is left exactly where it was.
 */
export function keepReachable(rect: Rect, area: Rect): Rect {
  const minX = area.x - Math.max(0, rect.w - REACHABLE_EDGE);
  const maxX = area.x + area.w - Math.min(rect.w, REACHABLE_EDGE);
  const maxY = area.y + area.h - Math.min(rect.h, REACHABLE_TOP);
  return {
    ...rect,
    x: Math.max(minX, Math.min(rect.x, maxX)),
    // The lower bound last, for the reason `drag.ts` gives: on a window taller than the area the
    // other order pins the title bar above the top of the screen, which is where this bug lived.
    y: Math.max(area.y, Math.min(rect.y, maxY)),
  };
}

/** Centre a size inside an area, rounded to whole pixels. */
export function centre(size: { w: number; h: number }, area: Rect): Rect {
  const w = Math.min(size.w, area.w);
  const h = Math.min(size.h, area.h);
  return {
    x: Math.round(area.x + (area.w - w) / 2),
    y: Math.round(area.y + (area.h - h) / 2),
    w,
    h,
  };
}

/**
 * Placement for the `index`-th window opened since boot. Steps down-and-right, then wraps back
 * to the origin — `cascade(CASCADE_LIMIT)` is exactly `cascade(0)` — so a long session never
 * marches windows off the bottom-right corner. Always clamped into the area.
 */
export function cascade(index: number, size: { w: number; h: number }, area: Rect): Rect {
  const step = ((index % CASCADE_LIMIT) + CASCADE_LIMIT) % CASCADE_LIMIT;
  const offset = step * CASCADE_STEP;
  return clampToDisplay(
    {
      x: area.x + CASCADE_ORIGIN + offset,
      y: area.y + CASCADE_ORIGIN + offset,
      w: size.w,
      h: size.h,
    },
    area,
  );
}

/**
 * Turn a §5.4 zone rect — fractions of the work area, 0..1 — into pixels. Here rather than in a
 * skin because `kernel/wm/snap.ts` needs it and may not import `skins/**` (§6.5); each skin's
 * policy re-exports it so its own callers read naturally.
 */
export function zoneRect(zoneFraction: Rect, area: Rect): Rect {
  return {
    x: Math.round(area.x + zoneFraction.x * area.w),
    y: Math.round(area.y + zoneFraction.y * area.h),
    w: Math.round(zoneFraction.w * area.w),
    h: Math.round(zoneFraction.h * area.h),
  };
}

/**
 * The rect to remember before a maximize or a snap. The *first* one wins: snapping left then
 * right then restoring must return to the rect the window had before any of it (§5.4).
 */
export function nextRestoreRect(current: Rect, existing: Rect | null): Rect {
  return existing ?? { ...current };
}

/**
 * Where a window lands when it comes back. With no `area` the stored rect is returned exactly;
 * with one it is clamped, so a window restored after the viewport shrank is still reachable.
 */
export function resolveRestore(
  current: Rect,
  restoreRect: Rect | null,
  area?: Rect,
  min?: { w: number; h: number },
): Rect {
  const target = restoreRect ?? current;
  return area ? clampToDisplay(target, area, min) : { ...target };
}
